import { Dimmer, Header, Loader, Segment } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { MODES_REGLEMENTS, MODES_REGLEMENTS_POINTAGE } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import { useReglementPointage } from '@/hooks';
import type { ModeReglementsPointageType, ReglementType } from '@/types';
import AucunPointageMessage from './AucunPointageMessage';
import ButtonGenerationBordereau from './ButtonGenerationBordereau';
import PointageModeReglement from './PointageModeReglement';
import ReglementListe from './ReglementListe';

const MAX_CHEQUES_SELECTIONNES = 200;

const PointageBordereaux = () => {
  const [modeReglementActif, setModeReglementActif] =
    React.useState<ModeReglementsPointageType | null>(null);
  const [selectedItems, setSelectedItems] = React.useState<ReglementType[]>([]);
  const currentReglementsRef = React.useRef<ReglementType[]>([]);

  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Filtrer les modes de pointage selon les modes autorisés de la régie
  const modesReglementsPointageFiltered = React.useMemo(
    () =>
      _.pickBy(MODES_REGLEMENTS_POINTAGE, (mode, code) =>
        _.some(regieModesReglement, (regieMode) => regieMode.code === code),
      ),
    [regieModesReglement],
  );

  const contextCheque = useReglementPointage(MODES_REGLEMENTS.CHEQUE);
  const contextNumeraire = useReglementPointage(MODES_REGLEMENTS.NUMERAIRE);
  const contextCESU = useReglementPointage(MODES_REGLEMENTS.CESU);
  const contextChequeVac = useReglementPointage(MODES_REGLEMENTS.CHEQUE_VAC);
  const contextCarteBancaire = useReglementPointage(MODES_REGLEMENTS.CARTE_BANCAIRE);
  const contextVirement = useReglementPointage(MODES_REGLEMENTS.VIREMENT);
  const contextPrelevement = useReglementPointage(MODES_REGLEMENTS.PRELEVEMENT);
  const contextCarteBancaireInternet = useReglementPointage(
    MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET,
  );
  const contextPrelevementInternet = useReglementPointage(MODES_REGLEMENTS.PRELEVEMENT_INTERNET);
  const contextTicket = useReglementPointage(MODES_REGLEMENTS.TICKET);
  const contextBonCCAS = useReglementPointage(MODES_REGLEMENTS.BON_CCAS);
  const contextBonCAF = useReglementPointage(MODES_REGLEMENTS.BON_CAF);

  const currentContext = React.useMemo(() => {
    switch (modeReglementActif) {
      case MODES_REGLEMENTS.CHEQUE:
        return contextCheque;
      case MODES_REGLEMENTS.NUMERAIRE:
        return contextNumeraire;
      case MODES_REGLEMENTS.CESU:
        return contextCESU;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return contextChequeVac;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return contextCarteBancaire;
      case MODES_REGLEMENTS.VIREMENT:
        return contextVirement;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return contextPrelevement;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return contextCarteBancaireInternet;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return contextPrelevementInternet;
      case MODES_REGLEMENTS.TICKET:
        return contextTicket;
      case MODES_REGLEMENTS.BON_CCAS:
        return contextBonCCAS;
      case MODES_REGLEMENTS.BON_CAF:
        return contextBonCAF;
      default:
        return null;
    }
  }, [
    modeReglementActif,
    contextCheque,
    contextNumeraire,
    contextCESU,
    contextChequeVac,
    contextCarteBancaire,
    contextVirement,
    contextPrelevement,
    contextCarteBancaireInternet,
    contextPrelevementInternet,
    contextTicket,
    contextBonCCAS,
    contextBonCAF,
  ]);

  const { reglements: currentReglements, reset: resetCurrentContext } = currentContext || {};

  const hasOneLoading = React.useMemo(
    () =>
      contextCheque.loading ||
      contextNumeraire.loading ||
      contextCESU.loading ||
      contextChequeVac.loading ||
      contextCarteBancaire.loading ||
      contextVirement.loading ||
      contextPrelevement.loading ||
      contextCarteBancaireInternet.loading ||
      contextPrelevementInternet.loading ||
      contextTicket.loading ||
      contextBonCCAS.loading ||
      contextBonCAF.loading,
    [
      contextCheque.loading,
      contextNumeraire.loading,
      contextCESU.loading,
      contextChequeVac.loading,
      contextCarteBancaire.loading,
      contextVirement.loading,
      contextPrelevement.loading,
      contextCarteBancaireInternet.loading,
      contextPrelevementInternet.loading,
      contextTicket.loading,
      contextBonCCAS.loading,
      contextBonCAF.loading,
    ],
  );

  const totalReglements = React.useMemo(
    () =>
      contextCheque.total +
      contextNumeraire.total +
      contextCESU.total +
      contextChequeVac.total +
      contextCarteBancaire.total +
      contextVirement.total +
      contextPrelevement.total +
      contextCarteBancaireInternet.total +
      contextPrelevementInternet.total +
      contextTicket.total +
      contextBonCCAS.total +
      contextBonCAF.total,
    [
      contextCheque.total,
      contextNumeraire.total,
      contextCESU.total,
      contextChequeVac.total,
      contextCarteBancaire.total,
      contextVirement.total,
      contextPrelevement.total,
      contextCarteBancaireInternet.total,
      contextPrelevementInternet.total,
      contextTicket.total,
      contextBonCCAS.total,
      contextBonCAF.total,
    ],
  );

  /**
   * On positionne l'utilisateur sur le prochain mode de règlement
   * ayant des règlements à pointer.
   * On considère que cette fonction est appelée quand le mode actuel
   * n'a plus de règlements à pointer.
   */
  const handleRedirectOnModeWithReglements = React.useCallback(() => {
    if (
      modeReglementActif !== MODES_REGLEMENTS.CHEQUE &&
      !_.isEmpty(contextCheque.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.CHEQUE]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.CHEQUE);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.NUMERAIRE &&
      !_.isEmpty(contextNumeraire.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.NUMERAIRE]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.NUMERAIRE);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.CESU &&
      !_.isEmpty(contextCESU.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.CESU]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.CESU);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.CHEQUE_VAC &&
      !_.isEmpty(contextChequeVac.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.CHEQUE_VAC]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.CHEQUE_VAC);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.CARTE_BANCAIRE &&
      !_.isEmpty(contextCarteBancaire.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.CARTE_BANCAIRE]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.CARTE_BANCAIRE);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.VIREMENT &&
      !_.isEmpty(contextVirement.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.VIREMENT]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.VIREMENT);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.PRELEVEMENT &&
      !_.isEmpty(contextPrelevement.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.PRELEVEMENT]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.PRELEVEMENT);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET &&
      !_.isEmpty(contextCarteBancaireInternet.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.PRELEVEMENT_INTERNET &&
      !_.isEmpty(contextPrelevementInternet.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.PRELEVEMENT_INTERNET]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.PRELEVEMENT_INTERNET);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.TICKET &&
      !_.isEmpty(contextTicket.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.TICKET]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.TICKET);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.BON_CCAS &&
      !_.isEmpty(contextBonCCAS.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.BON_CCAS]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.BON_CCAS);
    } else if (
      modeReglementActif !== MODES_REGLEMENTS.BON_CAF &&
      !_.isEmpty(contextBonCAF.reglements) &&
      modesReglementsPointageFiltered[MODES_REGLEMENTS.BON_CAF]
    ) {
      setModeReglementActif(MODES_REGLEMENTS.BON_CAF);
    }
  }, [
    contextCESU.reglements,
    contextCheque.reglements,
    contextChequeVac.reglements,
    contextNumeraire.reglements,
    contextCarteBancaire.reglements,
    contextVirement.reglements,
    contextPrelevement.reglements,
    contextCarteBancaireInternet.reglements,
    contextPrelevementInternet.reglements,
    contextTicket.reglements,
    contextBonCCAS.reglements,
    contextBonCAF.reglements,
    modeReglementActif,
    modesReglementsPointageFiltered,
  ]);

  const handleGenerate = React.useCallback(() => {
    // On réinitialise la sélection après génération
    setSelectedItems([]);
    if (_.size(selectedItems) === _.size(currentReglements)) {
      handleRedirectOnModeWithReglements();
    }

    // On force le rechargement de la liste courante
    resetCurrentContext();
  }, [currentReglements, handleRedirectOnModeWithReglements, selectedItems, resetCurrentContext]);

  const handleModeReglementChange = React.useCallback((e, { value }) => {
    setModeReglementActif(value);
    setSelectedItems([]);
  }, []);

  const handleReglementSelect = React.useCallback(
    (selectedReglement: ReglementType, selectedReglements: ReglementType[]) => {
      // Quoi qu'il arrive, les règlements dont le reçu est annulé resteront sélectionné
      const annules = _.filter(
        selectedItems,
        (reglement) => !_.isNil(reglement.dateAnnulationRecu),
      );

      // Si on a cliqué sur cocher tout / décocher tout
      if (_.isNil(selectedReglement)) {
        // Si on "déselectionne tout"
        if (_.isEmpty(selectedReglements)) {
          setSelectedItems(annules);
        } else if (modeReglementActif === MODES_REGLEMENTS.CHEQUE) {
          // Sinon on "sélectionne tout" en théorie
          const selectedItemsSansAnnules = _.filter(selectedItems, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          );

          // Mais on peut pas différencier :
          //    quand on veut sélectionner tout avec aucun élément préalablement sélectionné
          // et quand on veut sélectionner tout avec déjà des éléments sélectionnés
          // Donc on considère que si on "sélectionne tout" et qu'on a déjà atteint la limite
          // on déselectionne tout (sauf les annulés)
          if (selectedItemsSansAnnules.length >= MAX_CHEQUES_SELECTIONNES) {
            setSelectedItems(annules);
          } else {
            // on prends les 200 premiers reglements si c'est les chèques
            const currentReglementsValide = _.filter(currentReglements, (reglement) =>
              _.isNil(reglement.dateAnnulationRecu),
            );

            setSelectedItems([
              ...annules,
              ...currentReglementsValide.slice(0, MAX_CHEQUES_SELECTIONNES),
            ]);

            if (currentReglements.length > MAX_CHEQUES_SELECTIONNES) {
              toast(
                "La règlementation n'autorise pas la sélection de plus de 200 chèques par bordereau.",
                {
                  type: 'warning',
                },
              );
            }
          }
        } else {
          // Sinon si on "sélectionne tout" et que c'est pas les chèques, on prend tout
          setSelectedItems(currentReglements);
        }
        // Sinon si on sélectionne unitairement
      } else if (modeReglementActif === MODES_REGLEMENTS.CHEQUE) {
        // Si on sélectionne unitairement un règlement chèque
        // on limite la sélection à 200 éléments
        const selectedItemsSansAnnules = _.filter(selectedItems, (reglement) =>
          _.isNil(reglement.dateAnnulationRecu),
        );
        if (_.size(selectedItemsSansAnnules) >= MAX_CHEQUES_SELECTIONNES) {
          // Si on est en train de sélectionner, on le permet
          if (
            !_.some(
              selectedReglements,
              (reglement) => reglement.identifiant === selectedReglement.identifiant,
            )
          ) {
            setSelectedItems(selectedReglements);
          } else {
            // Si on essaye de sélectionner le 201ème, on fait rien
            // note : on copie quand même le tableau pour forcer le rerender
            // sinon on a un bug avec Liste qui laisse la case cochée
            // alors qu'on a pas ajouté l'élément dans notre state
            setSelectedItems([...selectedItems]);
            toast(
              "La règlementation n'autorise pas la sélection de plus de 200 chèques par bordereau.",
              {
                type: 'warning',
              },
            );
          }
        } else {
          // Sinon on ajoute l'élément
          setSelectedItems(selectedReglements);
        }
      } else {
        // Sinon on ajoute l'élément normalement
        setSelectedItems(selectedReglements);
      }
    },
    [currentReglements, modeReglementActif, selectedItems],
  );

  const handleRecuAnnulation = React.useCallback(() => {
    // Lorsqu'on annule un reçu, doit refresh toutes les listes
    // Vu qu'il peut y avoir aussi d'autres mode de règlement sur le reçu
    contextCheque.reset();
    contextNumeraire.reset();
    contextCESU.reset();
    contextChequeVac.reset();
    contextCarteBancaire.reset();
    contextVirement.reset();
    contextPrelevement.reset();
    contextCarteBancaireInternet.reset();
    contextPrelevementInternet.reset();
    contextTicket.reset();
    contextBonCCAS.reset();
    contextBonCAF.reset();
    setSelectedItems([]);
  }, [
    contextCESU,
    contextCheque,
    contextChequeVac,
    contextNumeraire,
    contextCarteBancaire,
    contextVirement,
    contextPrelevement,
    contextCarteBancaireInternet,
    contextPrelevementInternet,
    contextTicket,
    contextBonCCAS,
    contextBonCAF,
  ]);
  /**
   * Récupère le total du context adéquate.
   */
  const getTotalByCode = React.useCallback(
    (code: ModeReglementsPointageType) => {
      switch (code) {
        case MODES_REGLEMENTS.CHEQUE:
          return _.filter(contextCheque.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.NUMERAIRE:
          return _.filter(contextNumeraire.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.CESU:
          return _.filter(contextCESU.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.CHEQUE_VAC:
          return _.filter(contextChequeVac.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.CARTE_BANCAIRE:
          return _.filter(contextCarteBancaire.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.VIREMENT:
          return _.filter(contextVirement.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.PRELEVEMENT:
          return _.filter(contextPrelevement.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
          return _.filter(contextCarteBancaireInternet.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
          return _.filter(contextPrelevementInternet.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.TICKET:
          return _.filter(contextTicket.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.BON_CCAS:
          return _.filter(contextBonCCAS.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        case MODES_REGLEMENTS.BON_CAF:
          return _.filter(contextBonCAF.reglements, (reglement) =>
            _.isNil(reglement.dateAnnulationRecu),
          ).length;
        default:
          return 0;
      }
    },
    [
      contextCheque.reglements,
      contextNumeraire.reglements,
      contextCESU.reglements,
      contextChequeVac.reglements,
      contextCarteBancaire.reglements,
      contextVirement.reglements,
      contextPrelevement.reglements,
      contextCarteBancaireInternet.reglements,
      contextPrelevementInternet.reglements,
      contextTicket.reglements,
      contextBonCCAS.reglements,
      contextBonCAF.reglements,
    ],
  );

  // Initialiser le mode actif quand les modes autorisés sont chargés
  React.useEffect(() => {
    if (_.isNil(modeReglementActif)) {
      const premierMode = _.head(
        _.keys(modesReglementsPointageFiltered),
      ) as ModeReglementsPointageType;

      setModeReglementActif(premierMode);
    }
  }, [modeReglementActif, modesReglementsPointageFiltered]);

  // Mise à jour de la ref quand currentReglements change
  React.useEffect(() => {
    if (currentReglements) {
      currentReglementsRef.current = currentReglements;
    }
  }, [currentReglements]);

  // Quand la liste est chargée
  // On sélectionne par défaut tous les règlements dont le reçu
  // a été annulé
  React.useEffect(() => {
    if (!hasOneLoading && currentReglementsRef.current.length > 0) {
      const annules = _.filter(
        currentReglementsRef.current,
        (reglement) => !_.isNil(reglement.dateAnnulationRecu),
      );
      setSelectedItems(annules);
    }
  }, [hasOneLoading, modeReglementActif]);

  if (_.isNil(modeReglementActif)) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement des modes de règlement de la régie</Loader>
      </Dimmer>
    );
  }

  return (
    <Segment
      inverted
      secondary
      style={{
        flex: 1,
        overflow: 'auto',
        background: 'transparent',
      }}
    >
      <Segment
        style={{
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {!hasOneLoading && totalReglements === 0 && !_.isNil(currentReglements) ? (
          <AucunPointageMessage />
        ) : (
          <>
            <Header as="h1" style={{ alignSelf: 'center', marginBottom: '1em' }}>
              <Header.Content>
                Contrôle des règlements pour génération des bordereaux de remise
              </Header.Content>
            </Header>
            <PointageModeReglement
              modeReglementActif={modeReglementActif}
              loading={hasOneLoading}
              disabled={hasOneLoading}
              getTotalByCode={getTotalByCode}
              onModeReglementChange={handleModeReglementChange}
            />
            <ReglementListe
              type={modeReglementActif}
              onSelect={handleReglementSelect}
              selectedItems={selectedItems}
              onRecuAnnulation={handleRecuAnnulation}
            />
            <ButtonGenerationBordereau
              selectedReglements={selectedItems}
              modeReglementActif={modeReglementActif}
              onGenerate={handleGenerate}
            />
          </>
        )}
      </Segment>
    </Segment>
  );
};

export default PointageBordereaux;
